import {Injectable} from '@angular/core';
import {AbstractControl, FormArray, FormGroup} from '@angular/forms';
import {Observable, Subject} from 'rxjs';

const FIRST_INVALID_SELECTOR = [
  '.line-id-selector__field--invalid',
  '.bale-bot-selector__field--invalid',
  '.manual-contact-field--invalid',
  '.file-columns-mobile-field--invalid',
  '.upload-field--invalid',
  '.input-error',
  '.smsir-input-group--invalid',
  'input.ng-invalid',
  'textarea.ng-invalid',
  'textarea.smsir-textarea--invalid',
  'select.ng-invalid',
  '.ng-invalid[formcontrolname]',
  '.ng-invalid[formcontrol]',
  '[formcontrolname].ng-invalid',
  'smsir-dropdown.ng-invalid',
  'smsir-uploader.ng-invalid',
  '.schedule-week-field--invalid',
  '.schedule-date-range-field--invalid',
  '.schedule-send-at-field--invalid',
  '.smart-zone-presence-field--invalid',
  '.smart-zone-operators-field--invalid',
  '.smart-zone-map-field--invalid',
  '.smart-zone-send-total--invalid',
  '.bank-number-count-field--invalid',
  '.bank-number-start-from-field--invalid',
  '.composer-field--invalid',
  '.date-picker--invalid',
  '.radio-field--invalid',
  '.map-region__canvas--invalid',
].join(', ');

const SCROLL_PARENT_SELECTORS = [
  '.line-id-selector__field',
  '.bale-bot-selector__field',
  '.manual-contact-field',
  '.file-columns-mobile-field',
  '.upload-field',
  '.date-picker--invalid, app-date-time.date-picker--invalid',
  '.radio-field',
  '.smart-zone-map-field',
  '.smart-zone-operators-field',
  '.smart-zone-send-total',
  'smsir-input-group',
];

@Injectable({
  providedIn: 'root',
})
export class FormValidationHelperService {
  private readonly validationAttemptedSubject = new Subject<FormGroup | FormArray>();


  readonly validationAttempted$: Observable<FormGroup | FormArray> =
    this.validationAttemptedSubject.asObservable();

  public validateAndScroll(form: FormGroup | FormArray, container?: HTMLElement): boolean {
    this.markAllAsTouchedAndDirty(form);
    this.validationAttemptedSubject.next(form);

    if (form.valid) {
      return true;
    }

    this.scrollToFirstInvalid(container);
    return false;
  }

  public scrollToFirstInvalid(container?: HTMLElement): void {
    setTimeout(() => {
      const root = container ?? document;
      const firstInvalid = root.querySelector<HTMLElement>(FIRST_INVALID_SELECTOR);

      if (firstInvalid) {
        const scrollTarget = this.resolveScrollTarget(firstInvalid);
        scrollTarget.scrollIntoView({behavior: 'smooth', block: 'center'});

        const focusable = this.resolveFocusable(firstInvalid);
        focusable?.focus({preventScroll: true});
      }
    }, 120);
  }


  static belongsToFormRoot(
    control: AbstractControl | null | undefined,
    root: FormGroup | FormArray,
  ): boolean {
    if (!control) {
      return false;
    }

    if (control === root) {
      return true;
    }

    let parent = control.parent;
    while (parent) {
      if (parent === root) {
        return true;
      }
      parent = parent.parent;
    }

    return false;
  }

  public markAllAsTouchedAndDirty(control: AbstractControl): void {
    if (control instanceof FormGroup) {
      Object.values(control.controls).forEach((child) => this.markAllAsTouchedAndDirty(child));
    } else if (control instanceof FormArray) {
      control.controls.forEach((child) => this.markAllAsTouchedAndDirty(child));
    } else {
      control.markAsTouched({onlySelf: true});
      control.markAsDirty({onlySelf: true});
      // emitEvent: false — marking for submit must not re-fire valueChanges/statusChanges.
      // Those emissions were clearing form state (e.g. resets on radio valueChanges) on Confirm.
      control.updateValueAndValidity({onlySelf: true, emitEvent: false});
    }
  }

  private resolveScrollTarget(element: HTMLElement): HTMLElement {
    for (const selector of SCROLL_PARENT_SELECTORS) {
      const parent = element.closest(selector);
      if (parent instanceof HTMLElement) {
        return parent;
      }
    }

    const fieldWrapper = element.closest('[formcontrolname]');
    if (fieldWrapper instanceof HTMLElement && fieldWrapper !== element) {
      return fieldWrapper;
    }

    return element;
  }

  private resolveFocusable(element: HTMLElement): HTMLElement | null {
    if (element.matches('input, textarea, select, button, [tabindex="0"]')) {
      return element;
    }

    return (
      element.querySelector<HTMLElement>('input, textarea, select, button, [tabindex="0"]') ?? element
    );
  }
}
